import { useState, useEffect, useCallback } from 'react';
import { documentNodePermissionsService } from '../services/documentNodePermissionsService';

export interface DocumentPermissions {
  canDelete: boolean;
  canManagePermissions: boolean;
  canEdit: boolean;
  canCreate: boolean;
  canView: boolean;
  canActions: boolean;
}

export const useDocumentPermissions = (documentId: string | null) => {
  const [permissions, setPermissions] = useState<DocumentPermissions>({
    canDelete: false,
    canManagePermissions: false,
    canEdit: false,
    canCreate: false,
    canView: false,
    canActions: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const checkPermission = useCallback(async (actionName: string): Promise<boolean> => {
    if (!documentId || documentId === 'root') {
      return false;
    }

    try {
      return await documentNodePermissionsService.verifyPermission(
        documentId,
        'Documentos',
        actionName
      );
    } catch (error) {
      console.error(`Erro ao verificar permissão ${actionName}:`, error);
      return false;
    }
  }, [documentId]);

  const loadPermissions = useCallback(async () => {
    if (!documentId || documentId === 'root') {
      setPermissions({
        canDelete: false,
        canManagePermissions: false,
        canEdit: false,
        canCreate: false,
        canView: false,
        canActions: false
      });
      return;
    }

    setIsLoading(true);
    try {
      const [
        canDelete,
        canManagePermissions,
        canEdit,
        canCreate,
        canView,
        canActions
      ] = await Promise.all([
        checkPermission('Excluir Documentos'),
        checkPermission('Gerenciar Permissões'),
        checkPermission('Editar Documentos'),
        checkPermission('Criar Documentos'),
        checkPermission('Visualizar Documentos'),
        checkPermission('Gerenciar Ações')
      ]);

      setPermissions({
        canDelete,
        canManagePermissions,
        canEdit,
        canCreate,
        canView,
        canActions
      });
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      // Em caso de erro, define todas as permissões como false
      setPermissions({
        canDelete: false,
        canManagePermissions: false,
        canEdit: false,
        canCreate: false,
        canView: false,
        canActions: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [documentId, checkPermission]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const refreshPermissions = useCallback(() => {
    loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    isLoading,
    refreshPermissions,
    checkPermission
  };
};